import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { AccommodationsPrices } from '@apps/guest/packages/layout-engine/widgets/Custom/components/TravelMap/hooks/useAccommodationsPrices';
import { Maybe } from '@graphql/generated';

type HotelInfo = Readonly<{
  eventHandle: string;
  accommodationPlaceId: string;
  hotelName: string;
  venueName?: string;
  milesFromVenue?: string;
  customPage?: string;
}>;

interface HotelExtraInfo {
  hotelName?: string;
  isHotelPreferred?: boolean;
  hotelPrice?: number;
  hotelStrikeThroughPrice?: number;
  accommodationsType?: string;
  from?: string;
  customPage?: string;
}

const category = 'website';
const pagePrefix = 'admin';
const action = 'TravelButtonInteracted';

const actionProps = {
  action,
  category,
  pagePrefix,
};

const telemObject = createTelemetryObject({
  actions: {
    viewHotelPDPPage: (hotel: HotelInfo, prices?: AccommodationsPrices, available?: Maybe<boolean>, pageSlug?: string) => ({
      category: 'EventAccommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'ViewHotel',
        hotel,
        available,
        customPage: hotel.customPage,
        pageSlug,
        ...prices,
      },
    }),
    accommodationsLinkInteracted: (url: string, args: HotelExtraInfo, source: string = 'placeToStayComponent', pageSlug?: string) => ({
      action: 'LinkInteracted',
      category: 'linkify',
      label: 'accommodations',
      extraInfo: {
        actionType: 'click',
        url: url,
        source,
        type: 'url',
        customPage: args.customPage,
        pageSlug,
        ...args,
      },
    }),
    editItemClicked: (
      page: string,
      args: {
        isSuperAdmin?: boolean;
        accommodationPlaceId?: string;
        displayName?: string | null;
        customName?: string | null;
        url?: string | null;
        customUrl?: string | null;
        accommodationAction?: 'accommodationEdit' | 'accommodationSave';
        pageSlug: string;
      },
    ) => ({
      ...actionProps,
      page,
      label: 'Edit Item Clicked',
      extraInfo: {
        itemType: 'placeToStayComponent',
        ...args,
      },
    }),
    editItemSubmit: (
      page: string,
      args: {
        isSuperAdmin?: boolean;
        accommodationPlaceId?: string;
        zhId?: string | null;
        accommodationAction?: 'added' | 'edited';
        usingCustomLink?: boolean;
        customLink?: string;
        pageSlug: string;
      },
    ) => ({
      ...actionProps,
      page,
      category: 'admin.accommodations',
      label: 'Save',
      extraInfo: {
        itemType: 'placeToStayComponent',
        ...args,
      },
    }),
    editItemCancel: (page: string, args: { isSuperAdmin: boolean; pageSlug: string }) => ({
      ...actionProps,
      page,
      label: 'Edit Item Cancel',
      extraInfo: {
        itemType: 'placeToStayComponent',
        ...args,
      },
    }),
    deleteItemDialog: (page: string, args: { isSuperAdmin?: boolean; accommodationPlaceId?: string; pageSlug: string }) => ({
      ...actionProps,
      page,
      label: 'Delete Item Dialog',
      extraInfo: {
        itemType: 'placeToStayComponent',
        ...args,
      },
    }),
    addItemClicked: (page: string, pageSlug: string) => ({
      ...actionProps,
      page,
      label: 'Add Item Clicked',
      extraInfo: {
        itemType: 'placeToStayComponent',
        accommodationAction: 'Start Adding Stay',
        pageSlug,
      },
    }),
    hotelSearchCompleted: (
      page: string,
      args: {
        isMatch: boolean;
        matchSource?: 'joyCatalog' | 'googlePlacesOnly';
        joyPlaceId?: string | null;
        searchTerm: string;
        pageSlug: string;
      },
    ) => ({
      ...actionProps,
      page,
      label: 'Hotel Search Completed',
      extraInfo: {
        itemType: 'placeToStayComponent',
        ...args,
      },
    }),
  },
});

const { TelemetryProvider: PlaceToStayComponentTelemetryProvider, useTelemetry: usePlaceToStayComponentTelemetry } = createTelemetry(telemObject);

export { PlaceToStayComponentTelemetryProvider, usePlaceToStayComponentTelemetry };
